import {
  editAddress as _editAddress,
  createAddress as _newCreateAddress,
  deleteAddress as _newDeleteAddress,
  validateAddress as _validateAddress,
  validatePostalCode as _validatePostalCode,
  validateZipCodeStateCombination as _validateZipCodeStateCombination,
} from '@src/quince-site-common/src/services/addressService/addressService'
import { useCallback, useState } from 'react'
import { captureException } from '../components/Sentry/utils'
import { useTranslation } from 'next-i18next'

const getAdaptedAddressForNewCart = (address, stateOptions) => {
  return {
    addressLine1: address.address1,
    addressLine2: address.address2,
    city: address.city,
    countryCode: (address.country || 'US').toUpperCase(),
    firstName: address.firstName,
    lastName: address.lastName,
    phoneNumber: address.phone,
    provinceCode: stateOptions.find(state => state.stateName === address.province)?.stateCode || address.province,
    zip: address.zip,
  }
}

const getProvinceCode = (address, stateOptions) => {
  return stateOptions.find(state => state.stateName === address.province)?.stateCode || address.province
}

const mapFieldErrorsForAccountForm = fieldErrorMap => {
  if (!fieldErrorMap || typeof fieldErrorMap !== 'object') return {}
  const next = { ...fieldErrorMap }
  if (next.provinceCode) {
    next.province = next.provinceCode
    delete next.provinceCode
  }
  return next
}

const useCustomerAddress = stateOptions => {
  const [apiResponseErrors, setApiResponseErrors] = useState({})
  // eslint-disable-next-line i18next/no-literal-string
  const { t } = useTranslation('common')

  const validateZipCodeStateCombination = useCallback(
    async address => {
      try {
        setApiResponseErrors({})
        const provinceCode = getProvinceCode(address, stateOptions)
        const response = await _validateZipCodeStateCombination(address.zip, provinceCode, address?.country, t)
        if (!response.success) {
          const zipMessage = response.message || t('enter_a_valid_zip_code')
          const zipError = {
            zip: {
              message: zipMessage,
            },
          }
          setApiResponseErrors(zipError)
        }
        return response
      } catch (e) {
        captureException(
          e,
          { ...address },
          {
            // @i18n-ignore
            API: 'zip code validation service',
            type: 'API_FAILURE',
          },
        )
        return null
      }
    },
    [stateOptions, t],
  )

  const validatePostalCode = useCallback(
    async address => {
      try {
        const response = await _validatePostalCode(address.zip, address?.country, t)
        if (!response.success) {
          const zipError = {
            zip: {
              message: response.message || t('enter_a_valid_zip_code'),
            },
          }
          setApiResponseErrors(zipError)
        }
        return response
      } catch (e) {
        captureException(
          e,
          { ...address },
          {
            // @i18n-ignore
            API: 'postal code validation service',
            type: 'API_FAILURE',
          },
        )
        return null
      }
    },
    [t],
  )

  const validateAddress = useCallback(
    async (address, t, options) => {
      try {
        const adaptedAddressForNewCart = getAdaptedAddressForNewCart(address, stateOptions)
        const response = await _validateAddress(adaptedAddressForNewCart, t, options)
        setApiResponseErrors(mapFieldErrorsForAccountForm(response?.fieldErrorMap))
        return response
      } catch (e) {
        captureException(
          e,
          { ...address },
          {
            // @i18n-ignore
            API: 'validation service validateAddress',
            type: 'API_FAILURE',
          },
        )
        return null
      }
    },
    [stateOptions],
  )

  const newCreateAddress = useCallback(
    async (address, defaultAddress, enrichedAddress) => {
      try {
        const adaptedAddressForNewCart = getAdaptedAddressForNewCart(address, stateOptions)
        const addressResponse = await _newCreateAddress(adaptedAddressForNewCart, defaultAddress, enrichedAddress)
        return { response: addressResponse, error: null, loading: false }
      } catch (error) {
        captureException(
          error,
          { ...address },
          {
            // @i18n-ignore
            API: 'customer service customerAddressCreate',
            type: 'API_FAILURE',
          },
        )
        return { response: null, error, loading: false }
      }
    },
    [stateOptions],
  )

  const newDeleteAddress = async (id, customerId) => {
    try {
      const addressResponse = await _newDeleteAddress(id, customerId)
      return { response: addressResponse, error: null, loading: false }
    } catch (error) {
      captureException(
        error,
        { id, customerId },
        {
          // @i18n-ignore
          API: 'customer service newDeleteAddress',
          type: 'API_FAILURE',
        },
      )
      return { response: null, error, loading: false }
    }
  }

  const newUpdateAddress = async (id, customerId, address, defaultAddress, enrichedAddress) => {
    try {
      const adaptedAddressForNewCart = getAdaptedAddressForNewCart(address, stateOptions)
      const addressResponse = await _editAddress(
        adaptedAddressForNewCart,
        id,
        customerId,
        defaultAddress,
        enrichedAddress,
      )
      return { response: addressResponse, error: null, loading: false }
    } catch (error) {
      captureException(
        error,
        { ...address },
        {
          // @i18n-ignore
          API: 'customer service newUpdateAddress',
          type: 'API_FAILURE',
        },
      )
      return { response: null, error, loading: false }
    }
  }

  const clearApiErrors = useCallback(() => setApiResponseErrors({}), [])

  return {
    newCreateAddress,
    newUpdateAddress,
    validateZipCodeStateCombination,
    validatePostalCode,
    stateOptions,
    newDeleteAddress,
    validateAddress,
    apiResponseErrors,
    clearApiErrors,
  }
}

export default useCustomerAddress
